import { z } from "zod";

// Status da ingestão MQTT (bridge no worker).
// Endpoint informativo: SEMPRE responde 200 com o estado no body — nunca 503.
// Deliberadamente separado do /health: o healthcheck do Swarm bate no /health
// e um 503 por broker externo degradado reciclaria as réplicas da API em loop.

export const MqttIngestStateSchema = z.enum(["connected", "degraded", "disabled", "unknown"]);
export type MqttIngestState = z.infer<typeof MqttIngestStateSchema>;

// Contadores de descarte observável — acumulados desde o boot da bridge.
export const MqttIngestCountersSchema = z.object({
  unknownSensor: z.number().int().nonnegative(),
  deletedSensor: z.number().int().nonnegative(),
  invalidPayload: z.number().int().nonnegative(),
  invalidTopic: z.number().int().nonnegative(),
  orgMismatch: z.number().int().nonnegative(),
  // Ingestão por CLP/Device (lote por cartão). default(0) p/ compat com
  // status persistido por bridges antigas (Redis JSON sem estas chaves).
  unknownDevice: z.number().int().nonnegative().default(0),
  deletedDevice: z.number().int().nonnegative().default(0),
  unknownCard: z.number().int().nonnegative().default(0),
  positionOutOfRange: z.number().int().nonnegative().default(0),
  nonNumericRaw: z.number().int().nonnegative().default(0),
  degenerateScale: z.number().int().nonnegative().default(0),
});
export type MqttIngestCounters = z.infer<typeof MqttIngestCountersSchema>;

// Shape persistido pela bridge na Redis key `opus:mqtt:bridge:status`
// (SET ... EX 30, heartbeat a cada 10s). Key ausente ⇒ API infere "unknown".
export const MqttBridgeStatusSchema = z.object({
  // "unknown" não é estado da BRIDGE: é o que a API infere quando a key não
  // existe — derivar por exclusão mantém os dois enums em sincronia.
  state: MqttIngestStateSchema.exclude(["unknown"]),
  since: z.string().datetime(),
  lastError: z.string().nullable(),
  updatedAt: z.string().datetime(),
  counters: MqttIngestCountersSchema,
});
export type MqttBridgeStatus = z.infer<typeof MqttBridgeStatusSchema>;

export const MQTT_BRIDGE_STATUS_REDIS_KEY = "opus:mqtt:bridge:status";

export const MqttIngestStatusSchema = z.object({
  state: MqttIngestStateSchema,
  since: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  updatedAt: z.string().datetime().nullable(),
  counters: MqttIngestCountersSchema.nullable(),
});
export type MqttIngestStatus = z.infer<typeof MqttIngestStatusSchema>;

export const MqttIngestStatusResponseSchema = z.object({
  data: MqttIngestStatusSchema,
  error: z.null(),
});
export type MqttIngestStatusResponse = z.infer<typeof MqttIngestStatusResponseSchema>;

// Self-check de contrato do assistente (catálogo ↔ dispatch ↔ navegação ↔ tools).
// Exposto em GET /system/assistant-selfcheck (autenticado). `ok:false` ⇒ drift = bug de contrato.
export const AssistantDriftTypeSchema = z.enum([
  "COMMAND_WITHOUT_DISPATCHER",
  "DISPATCHER_WITHOUT_COMMAND",
  "NAV_WITHOUT_ROUTE",
  "ROUTE_WITHOUT_NAV",
  "DESTRUCTIVE_WITHOUT_ENTITY_NAME",
  "MANIFEST_INVALID",
  "TOOL_WITHOUT_PROJECTION",
  "INDEX_COVERAGE_MISMATCH", // Índice fino por papel diverge do catálogo visível
]);
// Fonte única do tipo de drift (o self-check em apps/api deriva daqui — não re-declara a lista, senão
// um tipo novo só num lado faria o serializer da rota 500 justo ao reportar esse drift).
export type AssistantDriftType = z.infer<typeof AssistantDriftTypeSchema>;
export const AssistantSelfCheckReportSchema = z.object({
  ok: z.boolean(),
  drifts: z.array(z.object({ type: AssistantDriftTypeSchema, detail: z.string() })),
});
export type AssistantSelfCheckReport = z.infer<typeof AssistantSelfCheckReportSchema>;
export type AssistantDrift = AssistantSelfCheckReport["drifts"][number];

export const AssistantSelfCheckResponseSchema = z.object({
  data: AssistantSelfCheckReportSchema,
  error: z.null(),
});
